import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import {
  CommentsListSchema,
  CoordinatesHistorySchema,
  File,
  FilesResponseSchema,
  GetProcedureFileFramesResponseSchema,
  ProcedureListItemSchema,
  ProcedureSchema,
} from '@/api/schema/procedures';
import api from '@/api/api';
import { withPaginationSchema } from '@/api/utils';
import { parseSearchParams } from '@/api/utils';
import { PaginationParams } from '@/api/types';
import { ProcedureLogTimelineResponseSchema } from '../schema/procedureLogTimeline';

const FILE_LINKS_REFETCH_INTERVAL_MS = 10 * 60 * 1000; // BE invalidates file urls after 10min

export type ProcedureFilesResponse = File[];
export type ProceduresParams = {
  humanReadableId?: string;
  eligibilityStatus?: string;
  procedureDate?: string;
  procedureStatus?: string;
  [key: string]: string | number | boolean | undefined;
} & PaginationParams;

export function getProceduresQueryOptions(params: ProceduresParams) {
  return queryOptions({
    queryKey: [ 'procedures', params ],
    queryFn: async () => {
      const response = await api.get('procedures', {
        searchParams: parseSearchParams(params),
      });
      return withPaginationSchema(ProcedureListItemSchema).parse(
        await response.json(),
      );
    },
    placeholderData: keepPreviousData,
  });
}

export function getProcedureQueryOptions(id: string, refetchInterval: number | undefined = undefined) {
  return queryOptions({
    queryKey: [ 'procedures', id ],
    refetchInterval,
    queryFn: async () => {
      const response = await api.get(`procedures/${id}`);
      return ProcedureSchema.parse(await response.json());
    },
  });
}

export function getProcedureFilesQueryOptions(procedureId: string) {
  return queryOptions({
    queryKey: [ 'procedures', 'files', procedureId ],
    queryFn: async () => {
      const response = await api.get(`procedures/${procedureId}/files`);
      return FilesResponseSchema.parse(await response.json());
    },
    refetchInterval: FILE_LINKS_REFETCH_INTERVAL_MS,
  });
}

export function getCoordinatesHistory(procedureTargetId: string | undefined) {
  return queryOptions({
    queryKey: [ 'procedure-targets', procedureTargetId, 'history' ],
    queryFn: async () => {
      const response = await api.get(
        `procedure-targets/${procedureTargetId}/coordinates/history`,
      );
      return CoordinatesHistorySchema.parse(await response.json());
    },
    enabled: !!procedureTargetId,
  });
}

export function getComments(procedureId: string | undefined) {
  return queryOptions({
    queryKey: [ 'procedures', procedureId, 'comments' ],
    queryFn: async () => {
      const response = await api.get(
        `procedures/${procedureId}/comments`,
      );
      return CommentsListSchema.parse(await response.json());
    },
  });
}

export const getProcedureLogTimelineQueryOptions = (procedureId: string) => queryOptions({
  queryKey: [ 'procedureLogTimeline', procedureId ],
  queryFn: async () => {
    const response = await api.get(`procedures/${procedureId}/events`);
    const json = await response.json();
    return ProcedureLogTimelineResponseSchema.parse(json);
  },
});

export const getProcedureFileFramesQueryOptions = (fileId: string) => queryOptions({
  queryKey: [ 'procedureFileFrames', fileId ],
  queryFn: async () => {
    const response = await api.get(`procedures/files/${fileId}/frames`);
    return GetProcedureFileFramesResponseSchema.parse(await response.json());
  },
});
